import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrderPages, approveOrder, unapproveOrder, signOrder, cancelOrder, type OrderFlow, type OrderRecord } from '@/api/order';
import { applyBack } from '@/api/back';
import { getAuthDepartments, getMember } from '@/api/auth';
import { useCart } from '@/hooks/useCart';
import { getUser, setUser } from '@/utils/auth';
import SearchableSelect from '@/components/base/SearchableSelect';
import SpecFilterSelect, { type SpecFilterOption } from '@/components/base/SpecFilterSelect';
import DateRangePicker from '@/components/base/DateRangePicker';
import Pagination from '@/components/base/Pagination';
import ConfirmModal from './components/ConfirmModal';
import CancelOrderModal from './components/CancelOrderModal';
import SignReceiptModal from './components/SignReceiptModal';
import SignVoucherModal from './components/SignVoucherModal';
import AfterSaleModal from './components/AfterSaleModal';
import ApproveModal from './components/ApproveModal';
import EditOrderModal from './components/EditOrderModal';

import type { ApprovalStep } from '@/types/order';

export type { ApprovalStep };

const PAGE_SIZE = 20;

function mapApproveState(state: number | string | undefined): ApprovalStep['status'] {
  const n = Number(state);
  if (n === 1) return 'approved';
  if (n === 2) return 'rejected';
  return 'pending';
}

/** 将订单 flows 转为审批步骤；过滤未到达且已结束的后续节点 */
function mapFlowsToSteps(flows: OrderFlow[] | undefined): ApprovalStep[] {
  if (!Array.isArray(flows) || flows.length === 0) return [];

  return flows
    .filter((flow) => {
      const sort = Number(flow.sort ?? 0);
      const nowSort = Number(flow.now_step_sort ?? 0);
      const isOver = Number(flow.is_over ?? 0);
      return sort <= nowSort || isOver !== 1;
    })
    .map((flow, index) => {
      const status = mapApproveState(flow.approve_state);
      const stepName = String(flow.step_name || flow.step || `第${index + 1}级审批`);
      const approverName =
        status === 'pending'
          ? String(flow.account_names || flow.account_name || '待定')
          : String(flow.account_name || flow.account_names || '');

      return {
        id: String(flow.id ?? `flow-${index}`),
        level: Number(flow.sort ?? index + 1) || index + 1,
        approverName,
        role: stepName,
        status,
        approvedAt: flow.approve_time ? String(flow.approve_time) : undefined,
        comment: flow.approve ? String(flow.approve) : undefined,
      };
    });
}

export interface OrderItemView {
  productId: string;
  /** 商品 ID */
  goodsId: string;
  /** 规格 ID */
  specId: string;
  name: string;
  /** 规格型号 */
  spec: string;
  quantity: number;
  unitPrice: number;
  unit: string;
  itemRemark: string;
  image?: string;
}

export interface OrderView {
  id: string;
  /** 数据库主键，审批接口使用 */
  rawId: string;
  /** 下单人账号 ID */
  accountId: string;
  orderPerson: string;
  department: string;
  departmentId: string;
  totalAmount: number;
  status: string;
  stateLabel: string;
  /** 展示用状态文案（如退货完成） */
  displayState: string;
  isBackorder: boolean;
  remark: string;
  createdAt: string;
  approvedAt?: string;
  shippedAt?: string;
  completedAt?: string;
  items: OrderItemView[];
  approvalSteps: ApprovalStep[];
  rejectReason?: string;
  /** 当前用户是否可审批本单 */
  canApprove: boolean;
  /** 当前用户是否可修改本单（is_canedit + 可编辑状态 + 当前审批节点） */
  canEdit: boolean;
  /** 当前用户是否可反审（须为最近已通过步骤的审批人） */
  canReverse: boolean;
  /** 签收凭证图片 URL */
  signImages: { id: string; uri: string }[];
}

/** 当前审批节点 account_ids 是否包含用户 */
function accountIdsInclude(accountIds: string | null | undefined, userId: string | number | null | undefined) {
  if (userId == null || userId === '') return false;
  const uid = String(userId).trim();
  if (!uid) return false;
  return String(accountIds || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .some((id) => id === uid);
}

/** 取当前审批节点：sort === now_step_sort */
function getCurrentFlow(flows: OrderFlow[] | undefined): OrderFlow | null {
  if (!Array.isArray(flows) || flows.length === 0) return null;
  return (
    flows.find((f) => Number(f.sort) === Number(f.now_step_sort)) || null
  );
}

function resolveCanApprove(raw: OrderRecord, userId: string | number | null | undefined) {
  const state = String(raw.state || '');
  if (state !== '待审批' && state !== '审批中') return false;
  const current = getCurrentFlow(raw.flows);
  if (!current || Number(current.approve_state) !== 0) return false;
  return accountIdsInclude(current.account_ids, userId);
}

/** 修改权限：is_canedit + 订单可编辑状态 + 当前账号属于当前审批节点 */
function resolveCanEdit(raw: OrderRecord, userId: string | number | null | undefined) {
  const user = getUser();
  const uid = userId ?? user?.id;

  // 1. 账号具备修改权限
  const canEditAccount =
    user?.is_canedit === true || user?.is_canedit === 1 || user?.is_canedit === '1';
  if (!canEditAccount) return false;

  // 2. 订单处于可编辑状态（审批未结束，出库前）
  const state = String(raw.state || '');
  if (state !== '待审批' && state !== '审批中') return false;

  // 3. 当前账号属于当前审批节点（sort === now_step_sort 且未审）
  const current = getCurrentFlow(raw.flows);
  if (!current || Number(current.approve_state) !== 0) return false;
  return accountIdsInclude(current.account_ids, uid);
}

/**
 * 反审权限：订单须为「已审批」，且当前账号属于最近一次已通过步骤的审批人
 *（与后端 FlowInstanceService::reverse 一致）
 */
function resolveCanReverse(raw: OrderRecord, userId: string | number | null | undefined) {
  if (String(raw.state || '') !== '已审批') return false;
  const flows = Array.isArray(raw.flows) ? raw.flows : [];
  const lastPassed = [...flows]
    .filter((f) => Number(f.approve_state) === 1)
    .sort((a, b) => Number(b.sort ?? 0) - Number(a.sort ?? 0))[0];
  if (!lastPassed) return false;
  return accountIdsInclude(lastPassed.account_ids, userId);
}

/** 当前登录用户是否为该订单下单人 */
function isOrderOwner(accountId: string | number | null | undefined, userId?: string | number | null) {
  const uid = userId ?? getUser()?.id;
  if (uid == null || uid === '' || accountId == null || accountId === '') return false;
  return String(uid) === String(accountId);
}

/** 企业允许退货天数（enterprise.latest），默认 7 */
function getEnterpriseReturnDays(user = getUser()) {
  const raw = Number(user?.enterprise?.latest);
  if (Number.isFinite(raw)) return raw;
  return 7;
}

/** 当前登录用户是否与订单同科室 */
function isSameDepartment(departmentId: string | number | null | undefined, user = getUser()) {
  const userDeptId = user?.department_id ?? user?.department?.id;
  if (userDeptId == null || userDeptId === '' || departmentId == null || departmentId === '') {
    return false;
  }
  return String(userDeptId) === String(departmentId);
}

/**
 * 是否仍可申请售后：完成时间起算，未超过企业 latest 天数
 * latest <= 0 视为未开放售后
 */
function canApplyAfterSale(completedAt?: string, latestDays = getEnterpriseReturnDays()) {
  if (!completedAt) return false;
  if (!Number.isFinite(latestDays) || latestDays <= 0) return false;
  const completeTs = new Date(completedAt).getTime();
  if (!Number.isFinite(completeTs)) return false;
  const deadlineTs = completeTs + latestDays * 24 * 60 * 60 * 1000;
  return Date.now() <= deadlineTs;
}

const statusMap: Record<string, { label: string; icon: string }> = {
  pending: { label: '待审批', icon: 'ri-time-line' },
  approving: { label: '审批中', icon: 'ri-loader-4-line' },
  approved: { label: '已审批', icon: 'ri-check-line' },
  shipped: { label: '出库中', icon: 'ri-truck-line' },
  completed: { label: '已完成', icon: 'ri-checkbox-circle-line' },
  rejected: { label: '已拒绝', icon: 'ri-close-circle-line' },
  cancelled: { label: '已取消', icon: 'ri-close-line' },
};

/** API 中文 state → 前端 status key */
const STATE_TO_STATUS: Record<string, string> = {
  待审批: 'pending',
  待确认: 'pending',
  审批中: 'approving',
  已审批: 'approved',
  待发货: 'approved',
  出库中: 'shipped',
  已签收: 'shipped',
  已完成: 'completed',
  已拒绝: 'rejected',
  已取消: 'cancelled',
  退货中: 'shipped',
};

/** 前端筛选 tab → API state 数组 */
const STATUS_TO_STATES: Record<string, string[]> = {
  pending: ['待审批', '待确认'],
  approving: ['审批中'],
  approved: ['已审批', '待发货'],
  shipped: ['出库中', '已签收'],
  completed: ['已完成'],
  rejected: ['已拒绝'],
};

/** 参考 Vue Tag 配色 */
function getOrderStateTag(state: string, isBackorder = false): { color: string; label: string } {
  if (state === '已拒绝') return { color: '#ed4014', label: state };
  if (state === '已取消') return { color: '#c5c8ce', label: state };
  if (state === '已审批') return { color: '#2d8cf0', label: state };
  if (state === '出库中') return { color: '#f90', label: state };
  if (state === '已完成' && !isBackorder) return { color: '#19be6b', label: state };
  if (state === '退货中' && isBackorder) return { color: '#f90', label: state };
  if (state === '已完成' && isBackorder) return { color: '#28CA9D', label: '退货完成' };
  return { color: '#33bcb7', label: state || '未知' };
}

/** 退货订单 state → 已完成步骤数（提交申请 / 售后确认 / 退换货 / 售后完成） */
function getBackOrderCompletedCount(state: string): number {
  if (state === '已完成') return 4;
  if (state === '退货中') return 2;
  return 2;
}

const statusFilters = [
  { id: 'all', label: '全部' },
  { id: 'todo', label: '我的代办' },
  { id: 'pending', label: '待审批' },
  { id: 'approving', label: '审批中' },
  { id: 'approved', label: '已审批' },
  { id: 'shipped', label: '出库中' },
  { id: 'completed', label: '已完成' },
  { id: 'rejected', label: '已拒绝' },
];

const statusOrderIndex: Record<string, number> = {
  pending: 1,
  approving: 1,
  approved: 2,
  shipped: 3,
  completed: 4,
  rejected: 1,
  cancelled: 1,
};

type DotTone = 'done' | 'rejected' | 'current' | 'pending';

function mapOrderRecord(raw: OrderRecord, userId?: string | number | null): OrderView {
  const state = String(raw.state || '');
  const status = STATE_TO_STATUS[state] || 'pending';
  const amount = Number(raw.total ?? raw.amount ?? 0);
  const items: OrderItemView[] = (raw.lists || []).map((row) => ({
    productId: String(row.spec_id || row.goods_id || row.id || ''),
    goodsId: String(row.goods_id || ''),
    specId: String(row.spec_id || ''),
    name: row.goods_name || '未命名商品',
    spec: row.sku_name || '',
    quantity: Number(row.quantity ?? 0) || 0,
    unitPrice: Number(row.price ?? 0) || 0,
    unit: row.unit || '件',
    itemRemark: row.remark || '',
    image: row.goods?.m_pic || '',
  }));

  const approvalSteps = mapFlowsToSteps(raw.flows);
  const rejectedFlow = (raw.flows || []).find((f) => Number(f.approve_state) === 2);
  const isBackorder = Number(raw.is_backorder) === 1;
  const stateTag = getOrderStateTag(state, isBackorder);

  return {
    id: String(raw.order_sn || raw.id),
    rawId: String(raw.id),
    accountId: String(raw.account_id || ''),
    orderPerson: raw.account_name || '',
    department: raw.office?.title || '',
    departmentId: String(raw.department_id || raw.office?.id || ''),
    totalAmount: Number.isFinite(amount) ? amount : 0,
    status,
    stateLabel: state || statusMap[status]?.label || state,
    displayState: stateTag.label,
    isBackorder,
    remark: raw.remark || '',
    createdAt: String(raw.created_at || ''),
    approvedAt: raw.approve_time ? String(raw.approve_time) : undefined,
    shippedAt: raw.delivery_time ? String(raw.delivery_time) : undefined,
    completedAt: raw.complete_time ? String(raw.complete_time) : undefined,
    items,
    approvalSteps,
    rejectReason: rejectedFlow?.approve
      ? String(rejectedFlow.approve)
      : raw.reject_reason
        ? String(raw.reject_reason)
        : undefined,
    canApprove: resolveCanApprove(raw, userId ?? getUser()?.id),
    canEdit: resolveCanEdit(raw, userId ?? getUser()?.id),
    canReverse: resolveCanReverse(raw, userId ?? getUser()?.id),
    signImages: (raw.signimgs || [])
      .map((img, index) => ({
        id: String(img.id ?? `sign-${index}`),
        uri: String(img.uri || '').trim(),
      }))
      .filter((img) => !!img.uri),
  };
}

export default function Orders() {
  const navigate = useNavigate();
  const { addItem } = useCart();

  type ModalState =
    | { type: 'confirm-cancel'; orderId: string; rawId: string }
    | { type: 'confirm-rebuy'; orderId: string }
    | { type: 'confirm-reverse'; orderId: string; rawId: string }
    | { type: 'sign'; orderId: string; rawId: string; mode: 'sign' | 'proxy' }
    | { type: 'sign-voucher'; orderId: string }
    | { type: 'aftersale'; orderId: string; rawId: string }
    | { type: 'approve'; orderId: string; rawId: string }
    | { type: 'edit'; orderId: string; rawId: string }
    | null;
  const [modal, setModal] = useState<ModalState>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const [reverseSubmitting, setReverseSubmitting] = useState(false);
  const [signSubmitting, setSignSubmitting] = useState(false);
  const [afterSaleSubmitting, setAfterSaleSubmitting] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const [activeFilter, setActiveFilter] = useState('all');
  const [flowsCount, setFlowsCount] = useState(0);
  const [openApprovalId, setOpenApprovalId] = useState<string | null>(null);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedDepts, setSelectedDepts] = useState<string[]>([]);
  const [deptOptions, setDeptOptions] = useState<string[]>([]);
  const [deptIdMap, setDeptIdMap] = useState<Record<string, string>>({});
  const deptIdMapRef = useRef<Record<string, string>>({});
  const [selectedSpecs, setSelectedSpecs] = useState<SpecFilterOption[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  const [orderList, setOrderList] = useState<OrderView[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const refreshFlowsCount = useCallback(async () => {
    try {
      const res = await getMember();
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) return;
      const count = Number(res.flows_count ?? 0);
      setFlowsCount(Number.isFinite(count) && count > 0 ? count : 0);
      if (res.data) {
        const token = getUser()?.token;
        setUser({
          ...res.data,
          ...(token ? { token } : {}),
          is_canedit: res.data.is_canedit ?? res.is_canedit,
          is_divide: res.data.is_divide ?? res.is_divide,
        });
      }
    } catch {
      // ignore
    }
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);

  const user = getUser();
  const showDeptFilter =
    user?.is_divide === true || user?.is_divide === 1 || user?.is_divide === '1';

  // 避免把 deptIdMap 放进 fetch 依赖导致请求成功后又触发新请求（页面闪动）
  useEffect(() => {
    deptIdMapRef.current = deptIdMap;
  }, [deptIdMap]);

  // is_divide 账号：从接口拉取可筛选科室；否则不展示科室组件
  useEffect(() => {
    if (!showDeptFilter) {
      setDeptOptions([]);
      setDeptIdMap({});
      setSelectedDepts([]);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const res = await getAuthDepartments();
        const ok = res.error === false || Number(res.error) === 0;
        if (cancelled || !ok || !Array.isArray(res.data)) return;

        const nextMap: Record<string, string> = {};
        const nextOptions: string[] = [];
        res.data.forEach((item) => {
          const id = Number(item.id);
          const title = String(item.title || '').trim();
          if (!title || !Number.isFinite(id) || id <= 0) return;
          if (!nextMap[title]) {
            nextMap[title] = String(id);
            nextOptions.push(title);
          }
        });
        setDeptIdMap(nextMap);
        setDeptOptions(nextOptions);
      } catch {
        if (!cancelled) {
          setDeptOptions([]);
          setDeptIdMap({});
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showDeptFilter]);

  useEffect(() => {
    void refreshFlowsCount();
  }, [refreshFlowsCount]);

  const fetchOrders = useCallback(async (pageNum: number) => {
    setLoading(true);
    setLoadError('');
    try {
      const date_range =
        dateFrom || dateTo
          ? [
              dateFrom ? `${dateFrom} 00:00:00` : '1970-01-01 00:00:00',
              dateTo ? `${dateTo} 23:59:59` : '2999-12-31 23:59:59',
            ]
          : undefined;

      const isTodo = activeFilter === 'todo';
      const idMap = deptIdMapRef.current;
      const res = await getOrderPages({
        page: pageNum,
        pagenumber: PAGE_SIZE,
        search: searchKeyword.trim() || undefined,
        date_range,
        office_ids:
          !isTodo && selectedDepts.length > 0
            ? selectedDepts
                .map((title) => Number(idMap[title]))
                .filter((id) => Number.isFinite(id) && id > 0)
            : undefined,
        spec_ids:
          selectedSpecs.length > 0
            ? selectedSpecs
                .map((s) => Number(s.id))
                .filter((id) => Number.isFinite(id) && id > 0)
            : undefined,
        state:
          !isTodo && activeFilter !== 'all'
            ? STATUS_TO_STATES[activeFilter] || []
            : undefined,
        is_todo: isTodo || undefined,
      });

      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setLoadError(res.message || res.msg || '订单加载失败');
        setOrderList([]);
        setTotal(0);
        return;
      }

      const list = Array.isArray(res.data)
        ? res.data.map((row) => mapOrderRecord(row, getUser()?.id))
        : [];
      const nextTotal = Number(res.total ?? 0) || 0;
      setOrderList(list);
      setTotal(nextTotal);

      const pages = Math.max(1, Math.ceil(nextTotal / PAGE_SIZE));
      if (nextTotal > 0 && pageNum > pages) {
        setCurrentPage(pages);
      }
    } catch {
      setLoadError('订单加载失败，请稍后重试');
      setOrderList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [activeFilter, selectedDepts, selectedSpecs, dateFrom, dateTo, searchKeyword]);

  useEffect(() => {
    void fetchOrders(currentPage);
  }, [currentPage, fetchOrders]);

  // 筛选条件变化时回到第 1 页；若已在第 1 页则由上面的 fetchOrders 依赖变化触发请求
  useEffect(() => {
    setCurrentPage((p) => (p === 1 ? p : 1));
  }, [activeFilter, selectedDepts, selectedSpecs, dateFrom, dateTo, searchKeyword]);

  const modalOrder = modal
    ? orderList.find((o) => o.id === modal.orderId) ?? null
    : null;

  const handleSearch = () => {
    setSearchKeyword(searchInput);
  };

  const handleReset = () => {
    setSearchInput('');
    setSearchKeyword('');
    setDateFrom('');
    setDateTo('');
    setSelectedDepts([]);
    setSelectedSpecs([]);
    setActiveFilter('all');
    setCurrentPage(1);
  };

  const hasActiveFilters = !!(
    searchKeyword ||
    dateFrom ||
    dateTo ||
    selectedDepts.length > 0 ||
    selectedSpecs.length > 0 ||
    activeFilter !== 'all'
  );

  const dateQueryLabel = (() => {
    if (dateFrom && dateTo) return `${dateFrom} 至 ${dateTo}`;
    if (dateFrom) return `${dateFrom} 起`;
    if (dateTo) return `截至 ${dateTo}`;
    return '';
  })();

  const handleDeptChange = (value: string | string[]) => {
    setSelectedDepts(Array.isArray(value) ? value : value ? [value] : []);
  };

  const toggleApproval = (orderId: string) => {
    if (openApprovalId === orderId) {
      setOpenApprovalId(null);
      setPopupPos(null);
      return;
    }
    const trigger = triggerRefs.current[orderId];
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      // Ensure popup doesn't go off the right edge of viewport
      const popupWidth = 288; // w-72 = 18rem = 288px
      let left = rect.left;
      if (left + popupWidth > window.innerWidth - 16) {
        left = window.innerWidth - popupWidth - 16;
      }
      if (left < 12) left = 12;
      setPopupPos({
        top: rect.bottom + 4,
        left,
      });
    }
    setOpenApprovalId(orderId);
  };

  // Close popup on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const insidePopup = popupRef.current?.contains(target);
      const insideTrigger = openApprovalId ? triggerRefs.current[openApprovalId]?.contains(target) : false;
      if (!insidePopup && !insideTrigger) {
        setOpenApprovalId(null);
        setPopupPos(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openApprovalId]);

  // Close popup on scroll/resize
  useEffect(() => {
    const handleClose = () => {
      setOpenApprovalId(null);
      setPopupPos(null);
    };
    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('resize', handleClose);
    return () => {
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('resize', handleClose);
    };
  }, []);

  const formatTime = (time: string) =>
    new Date(time).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });

  const formatDate = (time: string) =>
    new Date(time).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-');

  const buildApprovalNodes = (order: OrderView) => {
    const steps: ApprovalStep[] = order.approvalSteps || [];
    const isRejected = order.status === 'rejected';
    const isCancelled = order.status === 'cancelled';
    const curIdx = statusOrderIndex[order.status] ?? 1;
    const inApproval = order.status === 'pending' || order.status === 'approving';

    const nodes: {
      id: string;
      title: string;
      subtitle?: string;
      statusText?: string;
      statusTone?: DotTone;
      time?: string;
      comment?: string;
      dot: DotTone;
    }[] = [];

    nodes.push({
      id: 'submit',
      title: '提交采购单',
      subtitle: order.orderPerson || undefined,
      time: order.createdAt,
      dot: 'done',
    });

    if (isCancelled) {
      nodes.push({
        id: 'cancelled',
        title: '订单已取消',
        subtitle: order.orderPerson || undefined,
        statusText: '已取消',
        statusTone: 'rejected',
        dot: 'rejected',
      });
      return nodes;
    }

    if (steps.length > 0) {
      let currentMarked = false;
      steps.forEach((step) => {
        let dot: DotTone;
        let statusText: string;
        let subtitle: string | undefined;

        if (step.status === 'approved') {
          // approve_state = 1
          dot = 'done';
          statusText = '已审批';
          subtitle = step.approverName ? `${step.approverName}（同意）` : undefined;
        } else if (step.status === 'rejected') {
          // approve_state = 2
          dot = 'rejected';
          statusText = '已拒绝';
          subtitle = step.approverName ? `${step.approverName}（拒绝）` : undefined;
        } else if (!currentMarked && inApproval) {
          // approve_state = 0，当前待审节点
          dot = 'current';
          statusText = '待审批';
          subtitle = step.approverName ? `等待 ${step.approverName} 审批` : undefined;
          currentMarked = true;
        } else {
          // approve_state = 0，后续待审
          dot = 'pending';
          statusText = '待审批';
          subtitle = step.approverName ? `等待 ${step.approverName} 审批` : undefined;
        }

        nodes.push({
          id: step.id || `appr-${step.level}`,
          title: step.role,
          subtitle,
          statusText,
          statusTone: dot,
          time: step.approvedAt,
          comment: step.comment,
          dot,
        });
      });
    } else if (!isRejected) {
      nodes.push({
        id: 'approved',
        title: '审批通过',
        time: order.approvedAt,
        dot: curIdx >= 2 ? 'done' : 'pending',
      });
    }

    if (!isRejected) {
      if (order.shippedAt || curIdx >= 3) {
        nodes.push({
          id: 'shipped',
          title: '已发货',
          time: order.shippedAt,
          dot: order.shippedAt || curIdx >= 3 ? 'done' : 'pending',
        });
      }
      if (order.completedAt || curIdx >= 4) {
        nodes.push({
          id: 'completed',
          title: '已完成',
          time: order.completedAt,
          dot: order.completedAt || curIdx >= 4 ? 'done' : 'pending',
        });
      }
    }

    return nodes;
  };

  /** 退货订单：垂直售后处理进度（样式对齐审批流程） */
  const buildBackOrderNodes = (order: OrderView) => {
    const completedCount = getBackOrderCompletedCount(order.stateLabel);
    const steps: {
      id: string;
      title: string;
      time?: string;
      subtitle?: string;
      doneText: string;
      currentText: string;
    }[] = [
      { id: 'submit', title: '提交申请', time: order.createdAt, doneText: '已提交', currentText: '进行中' },
      { id: 'check', title: '售后确认', time: order.createdAt, doneText: '已确认', currentText: '进行中' },
      {
        id: 'exchange',
        title: '退换货',
        subtitle: '线下进行退换货',
        doneText: '已完成',
        currentText: '处理中',
      },
      { id: 'done', title: '售后完成', time: order.completedAt, doneText: '已完成', currentText: '进行中' },
    ];

    return steps.map((step, index) => {
      const isDone = index < completedCount;
      const isCurrent = !isDone && index === completedCount;
      const dot: DotTone = isDone ? 'done' : isCurrent ? 'current' : 'pending';

      return {
        id: step.id,
        title: step.title,
        subtitle: isDone || isCurrent ? step.subtitle : undefined,
        statusText: isDone ? step.doneText : isCurrent ? step.currentText : '未开始',
        statusTone: dot,
        time: isDone || isCurrent ? step.time : undefined,
        comment: undefined as string | undefined,
        dot,
      };
    });
  };

  const getQuickActions = (order: OrderView) => {
    const actions: {
      key: string;
      label: string;
      icon: string;
      danger?: boolean;
      primary?: boolean;
      secondary?: boolean;
    }[] = [];
    if (order.canApprove) {
      actions.push({ key: 'approve', label: '审批', icon: 'ri-shield-check-line', primary: true });
    }

    if (order.canEdit) {
      actions.push({ key: 'edit', label: '修改', icon: 'ri-edit-line' });
    }

    const user = getUser();
    const isOwner = isOrderOwner(order.accountId, user?.id);
    const sameDepartment = isSameDepartment(order.departmentId, user);

    // 取消：仅审批中 + 下单人；出库后不可取消
    if (order.status === 'approving' && isOwner) {
      actions.push({ key: 'cancel', label: '取消', icon: 'ri-close-circle-line', danger: true });
    }

    if (order.canReverse) {
      actions.push({ key: 'reverse', label: '反审', icon: 'ri-arrow-go-back-line' });
    }

    // 签收：仅出库中；下单人签收，同科室非下单人可代收
    if (order.stateLabel === '出库中') {
      if (isOwner) {
        actions.push({ key: 'sign', label: '签收', icon: 'ri-check-double-line', primary: true });
      } else if (sameDepartment) {
        actions.push({ key: 'proxy-sign', label: '代收', icon: 'ri-user-received-line', secondary: true });
      }
    }

    if (order.stateLabel === '已签收' || order.stateLabel === '已完成') {
      actions.push({ key: 'sign-voucher', label: '签收凭证', icon: 'ri-image-line' });
    }

    // 售后：已完成 + 退货期限内 + 当前账号与订单同科室
    if (order.status === 'completed' && sameDepartment && canApplyAfterSale(order.completedAt)) {
      actions.push({ key: 'aftersale', label: '售后', icon: 'ri-service-line' });
    }

    // 再购：已完成 / 已取消，且当前账号为下单人
    if (isOwner && (order.status === 'completed' || order.status === 'cancelled' || order.status === 'rejected')) {
      actions.push({ key: 'rebuy', label: '再购', icon: 'ri-shopping-cart-line' });
    }
    return actions;
  };

  // Action handlers
  const handleAction = (order: OrderView, actionKey: string) => {
    switch (actionKey) {
      case 'approve':
        setModal({ type: 'approve', orderId: order.id, rawId: order.rawId });
        break;
      case 'cancel':
        setModal({ type: 'confirm-cancel', orderId: order.id, rawId: order.rawId });
        break;
      case 'sign-voucher':
        setModal({ type: 'sign-voucher', orderId: order.id });
        break;
      case 'sign':
        setModal({ type: 'sign', orderId: order.id, rawId: order.rawId, mode: 'sign' });
        break;
      case 'proxy-sign':
        setModal({ type: 'sign', orderId: order.id, rawId: order.rawId, mode: 'proxy' });
        break;
      case 'aftersale':
        if (!isSameDepartment(order.departmentId)) {
          showToast('订单不属于当前科室，无法申请售后', 'error');
          break;
        }
        if (!canApplyAfterSale(order.completedAt)) {
          const days = getEnterpriseReturnDays();
          showToast(
            days <= 0
              ? '当前企业未开放售后申请'
              : `订单完成已超过 ${days} 天，无法申请售后`,
            'error',
          );
          break;
        }
        setModal({ type: 'aftersale', orderId: order.id, rawId: order.rawId });
        break;
      case 'rebuy':
        setModal({ type: 'confirm-rebuy', orderId: order.id });
        break;
      case 'reverse':
        if (order.canReverse) {
          setModal({ type: 'confirm-reverse', orderId: order.id, rawId: order.rawId });
        }
        break;
      case 'edit':
        if (order.canEdit) {
          setModal({ type: 'edit', orderId: order.id, rawId: order.rawId });
        }
        break;
      default:
        break;
    }
  };

  const handleCancelConfirm = async (reason: string) => {
    if (!modal || modal.type !== 'confirm-cancel' || cancelSubmitting) return;
    setCancelSubmitting(true);
    try {
      const res = await cancelOrder({
        order_id: Number(modal.rawId) || modal.rawId,
        remark: reason,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '取消订单失败', 'error');
        return;
      }
      showToast(res.message || `订单 ${modal.orderId} 已取消`);
      setModal(null);
      void fetchOrders(currentPage);
    } catch {
      showToast('取消订单失败，请稍后重试', 'error');
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleReverseConfirm = async () => {
    if (!modal || modal.type !== 'confirm-reverse' || reverseSubmitting) return;
    setReverseSubmitting(true);
    try {
      const res = await unapproveOrder({
        order_id: Number(modal.rawId) || modal.rawId,
        remark: '反审退回',
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '反审失败', 'error');
        return;
      }
      showToast(res.message || '订单反审成功');
      setModal(null);
      void fetchOrders(currentPage);
      void refreshFlowsCount();
    } catch {
      showToast('反审失败，请稍后重试', 'error');
    } finally {
      setReverseSubmitting(false);
    }
  };

  const handleSignConfirm = async (imageUrls: string[]) => {
    if (!modal || modal.type !== 'sign') return;
    setSignSubmitting(true);
    try {
      const res = await signOrder({
        order_id: Number(modal.rawId) || modal.rawId,
        images: imageUrls,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '签收失败', 'error');
        return;
      }
      showToast(
        modal.mode === 'proxy'
          ? `订单 ${modal.orderId} 代收成功`
          : `订单 ${modal.orderId} 签收成功`,
      );
      setModal(null);
      void fetchOrders(currentPage);
    } catch {
      showToast('签收失败，请稍后重试', 'error');
    } finally {
      setSignSubmitting(false);
    }
  };

  const handleRebuyConfirm = () => {
    if (!modalOrder) return;
    modalOrder.items.forEach((item) => {
      const qty = Math.max(1, Math.floor(Number(item.quantity) || 1));
      for (let i = 0; i < qty; i++) {
        addItem({
          productId: item.productId,
          name: item.name,
          price: item.unitPrice,
          unit: item.unit,
          image: item.image || '',
          stock: -1,
          model: item.unit,
        });
      }
    });
    setModal(null);
    showToast('商品已加入购物车');
    setTimeout(() => navigate('/cart'), 600);
  };

  const handleAfterSaleSubmit = async (data: {
    orderId: string;
    products: Array<{
      productId: string;
      goodsId: string;
      specId: string;
      productName: string;
      quantity: number;
      unit: string;
      unitPrice: number;
    }>;
    type: '退货' | '换货';
    reason: string;
    description: string;
    images: string[];
  }) => {
    if (!modal || modal.type !== 'aftersale') return;

    const validProducts = data.products.filter(
      (p) =>
        Number(p.quantity) >= 1 &&
        Number(p.goodsId) > 0 &&
        Number(p.specId) > 0,
    );
    if (validProducts.length === 0) {
      showToast('请至少选择一件商品并填写售后数量', 'error');
      return;
    }

    setAfterSaleSubmitting(true);
    try {
      const res = await applyBack({
        order_id: Number(modal.rawId) || modal.rawId,
        type: data.type,
        specs: validProducts.map((p) => ({
          goods_id: Number(p.goodsId) || p.goodsId,
          spec_id: Number(p.specId) || p.specId,
          quantity: Number(p.quantity),
        })),
        reason: data.reason || undefined,
        remark: data.description || undefined,
        images: data.images.length > 0 ? data.images : undefined,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '售后申请失败', 'error');
        return;
      }
      const names = validProducts.map((p) => p.productName).join('、');
      showToast(res.message || `售后申请已提交：${names}`);
      setModal(null);
      void fetchOrders(currentPage);
    } catch {
      showToast('售后申请失败，请稍后重试', 'error');
    } finally {
      setAfterSaleSubmitting(false);
    }
  };

  const handleApproveSubmit = async (isAgree: boolean, remark: string) => {
    if (!modal || modal.type !== 'approve') return;
    setApproveSubmitting(true);
    try {
      const res = await approveOrder({
        order_id: Number(modal.rawId) || modal.rawId,
        is_agree: isAgree,
        remark,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '审批失败', 'error');
        return;
      }
      showToast(isAgree ? '已同意审批' : '已拒绝审批');
      setModal(null);
      void fetchOrders(currentPage);
      void refreshFlowsCount();
    } catch {
      showToast('审批失败，请稍后重试', 'error');
    } finally {
      setApproveSubmitting(false);
    }
  };

  // Get currently open order's data for the fixed popup
  const openOrder = openApprovalId
    ? orderList.find((o) => o.id === openApprovalId) ?? null
    : null;
  const openApprovalNodes = openOrder
    ? openOrder.isBackorder
      ? buildBackOrderNodes(openOrder)
      : buildApprovalNodes(openOrder)
    : [];

  return (
    <div className="flex-1 bg-background-100">
      <div className="px-4 pb-4 md:px-6 md:pb-6">
        <div className="bg-white rounded-lg border border-background-200 p-4 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap min-w-0">
              {showDeptFilter && (
                <div className="min-w-[14rem] max-w-xs flex-1 basis-[14rem]">
                  <SearchableSelect
                    multiple
                    options={deptOptions}
                    value={selectedDepts}
                    onChange={handleDeptChange}
                    placeholder="搜索 / 选择科室"
                    allLabel="全部科室"
                    icon="ri-hospital-line"
                    searchPlaceholder="继续搜索科室..."
                  />
                </div>
              )}

              <div className="min-w-[14rem] max-w-sm flex-1 basis-[16rem]">
                <SpecFilterSelect
                  value={selectedSpecs}
                  onChange={setSelectedSpecs}
                  placeholder="搜索 / 选择规格"
                  searchPlaceholder="继续搜索规格..."
                />
              </div>

              <div className="w-64 shrink-0">
                <DateRangePicker
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  onDateFromChange={setDateFrom}
                  onDateToChange={setDateTo}
                  placeholder="下单日期范围"
                />
              </div>

              <div className="flex items-center min-w-[12rem] max-w-sm flex-1 border border-background-200 rounded-md overflow-hidden bg-background-50 focus-within:border-primary-400 transition-colors">
                <input
                  type="text"
                  placeholder="搜索订单号、人员"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                  className="flex-1 py-2 px-3 text-sm outline-none bg-transparent min-w-0"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => { setSearchInput(''); setSearchKeyword(''); }}
                    className="px-2 text-foreground-400 hover:text-foreground-600 shrink-0"
                  >
                    <i className="ri-close-line" />
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 ml-auto justify-end shrink-0 flex-wrap">
              {dateQueryLabel && (
                <span className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full bg-primary-50 border border-primary-100 text-xs text-primary-700 max-w-full">
                  <i className="ri-calendar-check-line shrink-0" />
                  <span className="truncate">
                    当前查询时间：
                    <span className="font-medium">{dateQueryLabel}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setDateFrom('');
                      setDateTo('');
                    }}
                    className="w-5 h-5 flex items-center justify-center rounded-full text-primary-500 hover:bg-primary-100 hover:text-primary-700 transition-colors shrink-0"
                    title="清除日期筛选"
                  >
                    <i className="ri-close-line text-sm" />
                  </button>
                </span>
              )}
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-background-200 text-xs text-foreground-500 hover:text-foreground-700 hover:bg-background-50 transition-colors whitespace-nowrap"
                >
                  <i className="ri-refresh-line" />
                  重置
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 主内容卡片 */}
        <div className="bg-white rounded-lg border border-background-200 overflow-hidden">

          {/* 状态筛选 + 统计 */}
          <div className="px-5 pt-4 pb-3 border-b border-background-100 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 overflow-x-auto">
              {statusFilters.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setActiveFilter(f.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border whitespace-nowrap shrink-0 transition-colors ${
                    activeFilter === f.id
                      ? 'bg-primary-500 text-background-50 border-primary-500'
                      : 'border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {f.label}
                  {f.id === 'todo' && flowsCount > 0 && (
                    <span className="min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 text-center font-medium">
                      {flowsCount > 99 ? '99+' : flowsCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <span className="text-xs text-foreground-500 whitespace-nowrap">
              共 <strong className="text-foreground-900">{total}</strong> 条
            </span>
          </div>

          {/* 订单列表 */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-500 mb-3" />
              <p className="text-sm text-foreground-500">正在加载订单...</p>
            </div>
          ) : loadError ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 bg-background-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-error-warning-line text-2xl text-primary-500" />
              </div>
              <p className="text-sm text-foreground-500 mb-2">{loadError}</p>
              <button
                type="button"
                onClick={() => void fetchOrders(currentPage)}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                重新加载
              </button>
            </div>
          ) : orderList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 bg-background-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-file-list-3-line text-2xl text-foreground-400" />
              </div>
              <p className="text-sm text-foreground-500 mb-2">没有找到符合条件的订单</p>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  清除筛选条件
                </button>
              )}
            </div>
          ) : (
            <>
              {/* 表格 */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-[#f8f8f9] border-b border-background-200">
                      <th className="py-3 px-4 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap border-r border-background-200">日期</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap border-r border-background-200">订单号 / 科室</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap border-r border-background-200">状态</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap border-r border-background-200">商品明细</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold text-foreground-600 whitespace-nowrap border-r border-background-200">金额</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold text-foreground-600 whitespace-nowrap">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderList.map((order, idx) => {
                      const status = statusMap[order.status] || statusMap.pending;
                      const stateTag = getOrderStateTag(order.stateLabel, order.isBackorder);
                      const isOpen = openApprovalId === order.id;
                      const quickActions = getQuickActions(order);
                      const visibleActions = (() => {
                        const approveAction = quickActions.find((a) => a.key === 'approve');
                        const others = quickActions.filter((a) => a.key !== 'approve');
                        if (approveAction) return [approveAction, ...others.slice(0, 1)];
                        return others.slice(0, 2);
                      })();

                      return (
                        <tr
                          key={order.id}
                          className={`border-b border-background-100 transition-colors hover:bg-background-50/70 ${
                            idx % 2 === 0 ? 'bg-white' : 'bg-background-50/30'
                          }`}
                        >
                          {/* 日期 */}
                          <td className="py-3 px-4 text-center">
                            <span className="text-xs text-foreground-500 whitespace-nowrap">
                              {order.createdAt ? formatDate(order.createdAt) : '-'}
                            </span>
                          </td>

                          {/* 订单号 + 科室 + 人员 */}
                          <td className="py-3 px-4">
                            <div className="flex flex-col gap-0.5">
                              <span className="text-sm font-semibold text-foreground-900">{order.id}</span>
                              <div className="flex items-center gap-2 text-xs text-foreground-500">
                                <span className="inline-flex items-center gap-1">
                                  <i className="ri-hospital-line text-foreground-400" />
                                  {order.department || '-'}
                                </span>
                                <span className="w-px h-3 bg-background-300" />
                                <span>{order.orderPerson || '-'}</span>
                              </div>
                            </div>
                          </td>

                          {/* 状态 Badge */}
                          <td className="py-3 px-4">
                            <button
                              ref={(el) => { triggerRefs.current[order.id] = el; }}
                              type="button"
                              onClick={() => toggleApproval(order.id)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium cursor-pointer transition-opacity whitespace-nowrap hover:opacity-85"
                              style={{
                                backgroundColor: stateTag.color,
                                color: '#fff',
                                border: `1px solid ${stateTag.color}`,
                              }}
                            >
                              <i className={`${status.icon} text-xs`} />
                              {order.displayState || stateTag.label}
                              <i className={`text-[10px] transition-transform ${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
                            </button>
                          </td>

                          {/* 商品明细 */}
                          <td className="py-3 px-4">
                            <div className="flex flex-col gap-0.5 max-w-[280px]">
                              {order.items.slice(0, 3).map((item, iidx) => (
                                <div key={iidx} className="flex items-center justify-between text-xs gap-2">
                                  <span className="text-foreground-800 truncate flex-1 min-w-0">{item.name}</span>
                                  <span className="text-foreground-400 shrink-0 whitespace-nowrap">
                                    {item.quantity}{item.unit} × ¥{item.unitPrice.toFixed(2)}
                                  </span>
                                </div>
                              ))}
                              {order.items.length > 3 && (
                                <span className="text-xs text-foreground-400">
                                  还有 {order.items.length - 3} 项...
                                </span>
                              )}
                              {order.items.length === 0 && (
                                <span className="text-xs text-foreground-400">暂无明细</span>
                              )}
                            </div>
                          </td>

                          {/* 金额：退货订单以橙色负数展示 */}
                          <td className="py-3 px-4 text-right">
                            {order.isBackorder ? (
                              <span className="text-sm font-bold text-orange-600 whitespace-nowrap">
                                ¥-{Math.abs(order.totalAmount).toFixed(2)}
                              </span>
                            ) : (
                              <span className="text-sm font-bold text-primary-600 whitespace-nowrap">
                                ¥{order.totalAmount.toFixed(2)}
                              </span>
                            )}
                          </td>

                          {/* 操作按钮 */}
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {visibleActions.map((a) => (
                                <button
                                  key={a.key}
                                  type="button"
                                  onClick={() => handleAction(order, a.key)}
                                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border transition-colors cursor-pointer whitespace-nowrap ${
                                    a.primary
                                      ? 'border-primary-500 bg-primary-500 text-background-50 hover:bg-primary-600 hover:border-primary-600'
                                      : a.secondary
                                        ? 'border-secondary-500 bg-secondary-500 text-background-50 hover:bg-secondary-600 hover:border-secondary-600'
                                        : a.danger
                                          ? 'border-primary-200 text-primary-600 hover:bg-primary-50 hover:border-primary-300'
                                          : 'border-background-200 text-foreground-600 hover:bg-background-50 hover:border-primary-300 hover:text-primary-600'
                                  }`}
                                >
                                  <i className={`${a.icon} text-xs`} />
                                  {a.label}
                                </button>
                              ))}
                              <button
                                type="button"
                                onClick={() => navigate(`/order/${order.rawId}`)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-primary-200 text-primary-600 hover:bg-primary-50 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                详情
                                <i className="ri-arrow-right-s-line text-xs" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="px-5 py-3 border-t border-background-100">
                <Pagination
                  page={safePage}
                  pageSize={PAGE_SIZE}
                  total={total}
                  onChange={setCurrentPage}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ===== Fixed-position approval / after-sale progress popup ===== */}
      {openApprovalId && popupPos && openOrder && (
        <div
          ref={(el) => { popupRef.current = el; }}
          style={{ position: 'fixed', top: popupPos.top, left: popupPos.left }}
          className="z-[100] w-72 bg-background-50 rounded-lg border border-background-200 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-2 border-b border-background-200 flex items-center justify-between">
            <span className="text-xs font-semibold text-foreground-900">
              {openOrder.isBackorder ? '售后处理进度' : '审批流程'}
            </span>
            <button
              type="button"
              onClick={() => { setOpenApprovalId(null); setPopupPos(null); }}
              className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-400 hover:text-foreground-600 transition-colors"
            >
              <i className="ri-close-line text-xs" />
            </button>
          </div>
          <div className="px-3 py-2">
            <div className="relative">
              <div className="absolute left-[7px] top-1.5 bottom-2 w-px bg-background-200" />
              {openApprovalNodes.map((node, nidx) => {
                const isLast = nidx === openApprovalNodes.length - 1;
                return (
                  <div key={node.id} className={`relative flex gap-2.5 ${isLast ? '' : 'pb-3'}`}>
                    <div className="relative z-10 shrink-0">
                      <div
                        className={`w-3.5 h-3.5 rounded-full flex items-center justify-center ${
                          node.dot === 'done'
                            ? 'bg-accent-500'
                            : node.dot === 'rejected'
                              ? 'bg-primary-500'
                              : node.dot === 'current'
                                ? 'bg-primary-500 ring-2 ring-primary-100'
                                : 'bg-background-100 border border-background-300'
                        }`}
                      >
                        {node.dot === 'done' && <i className="ri-check-line text-[9px] text-background-50" />}
                        {node.dot === 'rejected' && <i className="ri-close-line text-[9px] text-background-50" />}
                        {node.dot === 'pending' && <div className="w-1 h-1 rounded-full bg-background-400" />}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 -mt-px">
                      <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5">
                        <span
                          className={`text-[11px] leading-relaxed ${
                            node.dot === 'pending'
                              ? 'text-foreground-400'
                              : node.dot === 'rejected'
                                ? 'text-primary-700 font-medium'
                                : 'text-foreground-800 font-medium'
                          }`}
                        >
                          {node.title}
                        </span>
                        {node.subtitle && (
                          <span className="text-[11px] text-foreground-400">{node.subtitle}</span>
                        )}
                        {node.statusText && (
                          <span
                            className={`inline-flex items-center gap-1 text-[11px] font-medium ${
                              node.statusTone === 'done'
                                ? 'text-accent-600'
                                : node.statusTone === 'rejected'
                                  ? 'text-primary-600'
                                  : node.statusTone === 'current'
                                    ? 'text-primary-600'
                                    : 'text-foreground-400'
                            }`}
                          >
                            {node.statusTone === 'current' && <i className="ri-loader-4-line animate-spin text-[10px]" />}
                            {node.statusText}
                          </span>
                        )}
                      </div>
                      {node.time && (
                        <span className="text-[11px] text-foreground-400 leading-relaxed block mt-0.5">
                          {formatTime(node.time)}
                        </span>
                      )}
                      {node.comment && (
                        <div className="mt-1.5 px-2 py-1 rounded bg-background-100 text-[11px] text-foreground-600 leading-relaxed">
                          <i className="ri-chat-quote-line text-foreground-400 mr-1" />
                          {node.comment}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {!openOrder.isBackorder && openOrder.status === 'rejected' && openOrder.rejectReason && (
            <div className="px-3 py-2 bg-amber-50 border-t border-amber-200">
              <p className="text-[11px] text-amber-700">
                <i className="ri-error-warning-line mr-1" />
                驳回原因：{openOrder.rejectReason}
              </p>
            </div>
          )}
        </div>
      )}

      {/* ===== Toast notification ===== */}
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-[120] px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
          toast.type === 'success' ? 'bg-accent-500 text-background-50' : 'bg-primary-500 text-background-50'
        }`}>
          <i className={toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} />
          {toast.message}
        </div>
      )}

      {/* ===== Edit order modal ===== */}
      <EditOrderModal
        isOpen={modal?.type === 'edit'}
        orderId={modal?.type === 'edit' ? modal.rawId : ''}
        orderSn={modal?.type === 'edit' ? modal.orderId : ''}
        onClose={() => setModal(null)}
        onSuccess={() => void fetchOrders(currentPage)}
      />

      {/* ===== Approve modal ===== */}
      <ApproveModal
        isOpen={modal?.type === 'approve'}
        orderSn={modalOrder?.id || (modal?.type === 'approve' ? modal.orderId : '')}
        department={modalOrder?.department}
        orderPerson={modalOrder?.orderPerson}
        createdAt={modalOrder?.createdAt}
        items={modalOrder?.items.map((item) => ({
          name: item.name,
          spec: item.spec,
          quantity: item.quantity,
          unit: item.unit,
        }))}
        submitting={approveSubmitting}
        onCancel={() => {
          if (!approveSubmitting) setModal(null);
        }}
        onApprove={(remark) => void handleApproveSubmit(true, remark)}
        onReject={(remark) => void handleApproveSubmit(false, remark)}
      />

      {/* ===== Confirm modal: Reverse ===== */}
      <ConfirmModal
        isOpen={modal?.type === 'confirm-reverse'}
        title="反审确认"
        message={`确认对订单 ${modal?.orderId || ''} 执行反审？反审后订单将回到审批中状态，可重新审批。`}
        confirmLabel="确认反审"
        danger
        loading={reverseSubmitting}
        onCancel={() => {
          if (!reverseSubmitting) setModal(null);
        }}
        onConfirm={() => void handleReverseConfirm()}
      />

      {/* ===== Cancel order modal ===== */}
      <CancelOrderModal
        isOpen={modal?.type === 'confirm-cancel'}
        orderId={modal?.type === 'confirm-cancel' ? modal.orderId : ''}
        submitting={cancelSubmitting}
        onCancel={() => {
          if (!cancelSubmitting) setModal(null);
        }}
        onConfirm={handleCancelConfirm}
      />

      {/* ===== Confirm modal: Rebuy ===== */}
      <ConfirmModal
        isOpen={modal?.type === 'confirm-rebuy'}
        title="再次购买"
        message={`确认将订单 ${modal?.orderId || ''} 中的所有商品重新加入购物车？确认后将自动跳转到购物车进行结算。`}
        confirmLabel="确认加入购物车"
        onCancel={() => setModal(null)}
        onConfirm={handleRebuyConfirm}
      />

      {/* ===== Sign voucher images ===== */}
      <SignVoucherModal
        isOpen={modal?.type === 'sign-voucher'}
        orderId={modal?.type === 'sign-voucher' ? modal.orderId : ''}
        images={modal?.type === 'sign-voucher' ? (modalOrder?.signImages || []) : []}
        onClose={() => setModal(null)}
      />

      {/* ===== Sign / Proxy Receipt modal ===== */}
      <SignReceiptModal
        isOpen={modal?.type === 'sign'}
        orderId={modal?.type === 'sign' ? modal.orderId : ''}
        mode={modal?.type === 'sign' ? modal.mode : 'sign'}
        submitting={signSubmitting}
        onCancel={() => {
          if (!signSubmitting) setModal(null);
        }}
        onConfirm={handleSignConfirm}
      />

      {/* ===== After-sale modal ===== */}
      {modal?.type === 'aftersale' && modalOrder && (
        <AfterSaleModal
          isOpen
          orderId={modal.orderId}
          items={modalOrder.items}
          submitting={afterSaleSubmitting}
          onCancel={() => {
            if (!afterSaleSubmitting) setModal(null);
          }}
          onSubmit={handleAfterSaleSubmit}
        />
      )}
    </div>
  );
}